import {useState, type MouseEvent} from 'react';
import {useParams} from 'react-router-dom';
import {CalendarWeekDayProps, ErrorObject} from './types';

import {
  getAreAllNodesClosed,
  getGroupWithWorkdayCountByDate,
  getStartOfTodayISOString,
  useAppSelector,
} from '@lib/store';

import {Date as DateAtom} from '@atoms';
import {WorkdayStatus} from '@lib/store/src/data/timeslots/apiTypes';
import {AvatarStack} from '@molecules';
import {cn, generateTestIdCoreString} from '@utils';
import {QuickNotWorkingButton} from './components/QuickNotWorkingButton';
import {QuickOfficeDayButton} from './components/QuickOfficeDayButton';
import {QuickWorkingRemotelyButton} from './components/QuickWorkingRemotelyButton';
import {CalendarCardCell} from './styles';
import {
  getIsOfficeDayBookableBasedOnQuota,
  getSortedConnectedUsersWithOfficeDayOnDate,
  getWorkdaysForDate,
} from '@lib/store/src/data/timeslots/selectors';
import {useMemoDate} from '@lib/store/src/ui/hooks/useMemoDate';
import {CalendarWeekDayLoader} from './CalendarWeekDayLoader';
import {BookingFailedLabel} from './components/BookingFailedLabel';
import {useModal} from '@hooks';
import {useWorkdayIsIncomplete} from '@lib/logic';

export const CalendarWeekDay = ({
  date: dateKey,
  isToday,
  toggleDayAndWeekView,
  showConnectionsWithIds,
  isLoading,
  ...props
}: CalendarWeekDayProps) => {
  const TEST_ID = generateTestIdCoreString(import.meta.url);

  const {isModalOpen} = useModal();
  const workdayData = useAppSelector(getWorkdaysForDate(dateKey));

  // Note: this bases the icon on the first workday scheduled on a day
  const workday = workdayData?.data?.at(0);

  const colleagues = useAppSelector((state) => getSortedConnectedUsersWithOfficeDayOnDate(state, dateKey));

  const date = useMemoDate(dateKey);
  const todayKey = useAppSelector(getStartOfTodayISOString);
  const isPast = dateKey < todayKey.slice(0, 'yyyy-MM-dd'.length);

  const activeDateKey = useParams().date;
  const isActive = dateKey === activeDateKey;

  const areAllNodesClosed = useAppSelector((state) => getAreAllNodesClosed(state, date));
  const isOfficeDayBookableBasedOnQuota = useAppSelector(getIsOfficeDayBookableBasedOnQuota(date));

  const dayOverviewLink = `/calendar/${dateKey}`;

  const filteredColleagues = colleagues.filter(({id}) =>
    showConnectionsWithIds.length ? showConnectionsWithIds.includes(id) : true,
  );

  const groupsWithWorkdayCount = useAppSelector((state) => getGroupWithWorkdayCountByDate(state, dateKey));

  const [loadingWorkplace, setLoadingWorkplace] = useState(false);
  const [loadingParking, setLoadingParking] = useState(false);
  const [errorObject, setErrorObject] = useState<ErrorObject>({
    workday: null,
    workplace: null,
    parking: null,
  });
  const [processingFor, setProcessingFor] = useState<WorkdayStatus | null>(null);

  const selectedWorkdayStatus = workday?.status || processingFor;

  const error = errorObject.workday || errorObject.workplace || errorObject.parking;

  const userIsEditingDay = isActive && isModalOpen;
  const isIncompleteBooking = useWorkdayIsIncomplete(workday ?? null) && !userIsEditingDay;

  if (isLoading) {
    return <CalendarWeekDayLoader />;
  }

  return (
    <CalendarCardCell
      style={{
        scrollMarginTop: 'var(--header-height)',
      }}
      className={cn('border-none transition-colors py-6 px-5', {
        'bg-mapiq-black-50': isActive,
      })}
      $isDisabled={isPast}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        if (error) {
          setErrorObject((p) => ({
            workday: null,
            workplace: null,
            parking: null,
          }));
        }
        if (isPast) {
          e.preventDefault();
        } else {
          toggleDayAndWeekView();
        }
      }}
      to={dayOverviewLink}
      data-scroll-to={dateKey}
      aria-disabled={isPast}
      tabIndex={isPast ? -1 : 0}
      data-testid={`${TEST_ID}-card-cell-${dateKey.split('T')[0]}`}
      {...props}>
      <div className="w-full flex gap-2 flex-col">
        <div className="flex gap-4 items-center justify-between">
          <h4>
            <DateAtom
              date={dateKey}
              format="EEEE"
            />
          </h4>

          <h4 className="text-mapiq-black-500">
            <DateAtom
              date={dateKey}
              format="d MMM"
            />
          </h4>
        </div>

        <div className="flex gap-4 items-center justify-between">
          <div className="flex gap-3 items-center">
            {(selectedWorkdayStatus === 'OfficeDay' || selectedWorkdayStatus === null) && (
              <QuickOfficeDayButton
                disabled={isPast || areAllNodesClosed || !isOfficeDayBookableBasedOnQuota}
                date={dateKey}
                processingFor={processingFor}
                setProcessingFor={setProcessingFor}
                loadingWorkplace={loadingWorkplace}
                setLoadingWorkplace={setLoadingWorkplace}
                loadingParking={loadingParking}
                setLoadingParking={setLoadingParking}
                errorObject={errorObject}
                setErrorObject={setErrorObject}
                isIncompleteBooking={isIncompleteBooking}
              />
            )}

            {(selectedWorkdayStatus === 'WorkingRemotely' || selectedWorkdayStatus === null) && (
              <QuickWorkingRemotelyButton
                disabled={isPast}
                date={dateKey}
                processingFor={processingFor}
                setProcessingFor={setProcessingFor}
                loading={loadingWorkplace}
                setLoading={setLoadingWorkplace}
                errorObject={errorObject}
                setErrorObject={setErrorObject}
              />
            )}

            {(selectedWorkdayStatus === 'NotWorking' || selectedWorkdayStatus === null) && (
              <QuickNotWorkingButton
                disabled={isPast}
                date={dateKey}
                processingFor={processingFor}
                setProcessingFor={setProcessingFor}
                loading={loadingWorkplace}
                setLoading={setLoadingWorkplace}
                errorObject={errorObject}
                setErrorObject={setErrorObject}
              />
            )}
          </div>

          <div className="flex items-center relative ">
            <small
              className={cn('text-functional-red z-1 absolute right-0 text-right text-nowrap', {
                'animate-in fade-in fill-mode-forwards': error,
                'animate-out fade-out fill-mode-forwards': !error,
              })}>
              {error}
            </small>

            {isIncompleteBooking && error === null && (
              <small className={cn('text-functional-red z-1 absolute right-0 text-right text-nowrap')}>
                <BookingFailedLabel />
              </small>
            )}

            {!isIncompleteBooking && (
              <AvatarStack
                className={cn({
                  'animate-in fade-in fill-mode-forwards': !error,
                  'animate-out fade-out fill-mode-forwards pointer-events-none': error,
                })}
                date={dateKey}
                disabled={isPast}
                users={filteredColleagues}
                groupsWithCounts={groupsWithWorkdayCount}
                overlap={true}
                max={3}
              />
            )}
          </div>
        </div>
      </div>
    </CalendarCardCell>
  );
};
